//@ts-nocheck
/*
  TODO: When new payment plans are baseline (flag retired):
  - Promote this module to the canonical calculator.
    • Option A: Rename this file to 'paymentSchedule.js' and export the
      public API as `getPaymentPlan` or similar.
    • Option B: Keep the filename but update all call sites to import
      from './paymentScheduleNew' directly and delete the adapter.
  - Remove the adapter-based branching and legacy util usage.
  - Delete legacy 'paymentSchedule.js' and its tests.
  - Rename tests to reflect the canonical API (e.g.,
    '__test__/paymentSchedule.test.js').
*/

// New payment schedule calculator for enableNewPaymentPlans
// - Trip-level rules are the source of truth.
// - Pax comes from each bookingplan's `occupancy`.
// - Deposit is due at booking creation time.
// - Interim installments (second/third/fourth) respect their configured
//   "days before departure" and are only due at booking if already past due.
// - Final payment date is derived from the days-before-start value.
// - All dates are date-only strings (YYYY-MM-DD), timezone-agnostic.

const LABEL_ORDER = {
  deposit: 1,
  second: 2,
  third: 3,
  fourth: 4,
  final: 5
};

const isValidDateString = (s) =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);

const parseYMDToUTC = (s) => {
  const [y, m, d] = s.split('-').map((n) => parseInt(n, 10));
  return new Date(Date.UTC(y, m - 1, d));
};

const formatUTCToYMD = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const compareYMD = (a, b) => {
  if (!isValidDateString(a) || !isValidDateString(b)) return NaN;
  const da = parseYMDToUTC(a).getTime();
  const db = parseYMDToUTC(b).getTime();
  return da - db;
};

const subtractDays = (ymd, days) => {
  const dt = parseYMDToUTC(ymd);
  dt.setUTCDate(dt.getUTCDate() - Number(days || 0));
  return formatUTCToYMD(dt);
};

const round2 = (n) => {
  const x = Number(n);
  if (!isFinite(x)) return 0;
  return Math.round((x + Number.EPSILON) * 100) / 100;
};

// Parses depositAmount string into a function that computes the deposit value from (total, pax)
// Accepted forms: "20%", "199.99pp", "500pb" (case-insensitive)
const parseDepositFormula = (depositAmount) => {
  if (typeof depositAmount !== 'string') return { type: 'zero', value: 0 };
  const raw = depositAmount.trim().toLowerCase();
  const m = raw.match(/^\s*([0-9]+(?:\.[0-9]+)?)\s*(%|pp|pb)\s*$/i);
  if (!m) return { type: 'zero', value: 0 };
  const amount = parseFloat(m[1]);
  const type = m[2];
  return { type, value: isNaN(amount) ? 0 : amount };
};

const computeDepositAmount = (formula, total, pax) => {
  const amt = Number(formula?.value ?? 0);
  if (!formula || !formula.type) return 0;
  if (amt <= 0) return 0;
  switch (formula.type) {
    case '%':
      return round2((Number(total) || 0) * (amt / 100));
    case 'pp':
      return round2((Number(pax) || 0) * amt);
    case 'pb':
      return round2(amt);
    default:
      return 0;
  }
};

/**
 * Parses a payment amount formula string (e.g., "250pp", "30%", "500pb")
 * Returns the parsed formula or null if invalid
 */
const parsePaymentAmountFormula = (formula) => {
  if (formula === null || formula === undefined || formula === '') return null;
  if (typeof formula !== 'number' && typeof formula !== 'string') return null;
  const trimmed =
    typeof formula === 'number' ? String(formula) : formula.trim();
  if (!trimmed) return null;

  // Match patterns: "123%", "123pp", "123pb", or just "123" (defaults to %)
  const match = trimmed.match(/^(\d+(?:\.\d+)?)(pp|pb|%)?$/i);
  if (!match) return null;

  const amount = parseFloat(match[1]);
  const type = match[2]?.toLowerCase() || '%'; // Default to percentage if no suffix

  return {
    type,
    value: isNaN(amount) ? 0 : amount
  };
};

/**
 * Checks if all configured payments (deposit and interim payments) are percentage-based
 * @param {Object} depositFormula - Parsed deposit formula
 * @param {Object|null} secondFormula - Parsed second payment formula (null if not configured)
 * @param {Object|null} thirdFormula - Parsed third payment formula (null if not configured)
 * @param {Object|null} fourthFormula - Parsed fourth payment formula (null if not configured)
 * @returns {boolean} true if deposit and all configured interim payments are percentage-based
 */
const areAllPaymentsPercentageBased = (
  depositFormula,
  secondFormula,
  thirdFormula,
  fourthFormula
) => {
  // Deposit must be percentage-based
  if (!depositFormula || depositFormula.type !== '%') {
    return false;
  }

  // Check each configured interim payment - if it exists, it must be percentage-based
  if (secondFormula && secondFormula.type !== '%') {
    return false;
  }
  if (thirdFormula && thirdFormula.type !== '%') {
    return false;
  }
  if (fourthFormula && fourthFormula.type !== '%') {
    return false;
  }

  return true;
};

/**
 * Computes the payment amount based on the formula type
 * - Percentage payments: When all payments are %-based, calculated against total price.
 *   Otherwise, calculated against post-deposit base (total - deposit).
 *   Always capped at remaining balance.
 * - Flat amounts (pp/pb): calculated independently, then capped at remaining balance
 * @param {Object} formula - Parsed formula with type and value
 * @param {number} postDepositBase - The base amount for percentage calculations (total - deposit)
 * @param {number} remainingBalance - Remaining balance after previous payments (for capping)
 * @param {number} pax - Number of passengers
 * @param {boolean} useGlobalBase - If true, use totalPrice as base for percentage calculations
 * @param {number} totalPrice - Total price (used when useGlobalBase is true)
 * @returns {number} Calculated payment amount (capped at remaining balance)
 */
const computePaymentAmount = (
  formula,
  postDepositBase,
  remainingBalance,
  pax,
  useGlobalBase = false,
  totalPrice = 0
) => {
  if (!formula || !formula.type) return 0;
  const amt = Number(formula?.value ?? 0);
  if (amt <= 0) return 0;

  // Calculate the raw amount based on formula type
  let calculatedAmount = 0;
  switch (formula.type) {
    case '%': {
      // Use totalPrice if all payments are percentage-based, otherwise use post-deposit base
      {
        const baseAmount = useGlobalBase ? totalPrice : postDepositBase;
        calculatedAmount = round2((Number(baseAmount) || 0) * (amt / 100));
      }
      break;
    }
    case 'pp':
      calculatedAmount = round2((Number(pax) || 0) * amt);
      break;
    case 'pb':
      calculatedAmount = round2(amt);
      break;
    default:
      return 0;
  }

  // Cap at remaining balance to prevent overpayment
  const cappedAmount = Math.min(calculatedAmount, remainingBalance);
  return round2(Math.max(cappedAmount, 0));
};

const coerceNumberOrNull = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return isFinite(n) ? n : null;
};

// Public API (new logic)
// @param {
//   tripStartDate?: string, // YYYY-MM-DD
//   bookingCreationDate: string, // YYYY-MM-DD
//   bookingPlans: Array<{
//     occupancy: number,
//     pricePerPerson: number,
//     bookingDeadline?: string // YYYY-MM-DD
//   }>,
//   paymentPlan: {
//     depositAmount: string, // e.g. "20%", "199.99pp", "500pb"
//     secondPaymentAmount?: string,
//     secondPaymentDue?: number,
//     thirdPaymentAmount?: string,
//     thirdPaymentDue?: number,
//     fourthPaymentAmount?: string,
//     fourthPaymentDue?: number,
//     finalPaymentDue: number,
//     enablePartialPayments?: boolean,
//     allowBookingWithoutPayment?: boolean
//   },
//   priceAdjustment?: number, // Additional price adjustment to add to total
//   bookingAddOns?: Array<{
//     pricePerPerson: number,
//     dueAtBooking?: boolean
//   }> // Add-ons selected for the booking
// }
// returns: {
//   canBook: boolean,
//   mustPayInFull: boolean,
//   dueAtBooking: number, // 2-decimal, same units as price inputs
//   schedule: Array<{ date, amount, label: 'final' }>,
//   totalPrice: number,
//   reasons?: string[]
// }
export const calculatePaymentScheduleNew = ({
  tripStartDate,
  bookingCreationDate,
  bookingPlans = [],
  paymentPlan: originalPaymentPlan,
  priceAdjustment = 0,
  bookingAddOns = []
}) => {
  // Handle missing or empty payment plan by defaulting to pay in full
  const paymentPlan = originalPaymentPlan || {
    depositAmount: '100%',
    finalPaymentDue: 0,
    enablePartialPayments: false,
    allowBookingWithoutPayment: false
  };

  // If deposit amount is empty or invalid, default to 100%
  if (!paymentPlan.depositAmount || paymentPlan.depositAmount.trim() === '') {
    paymentPlan.depositAmount = '100%';
  }
  const reasons = [];

  const hasStartDate = isValidDateString(tripStartDate);

  // Basic validation
  if (!isValidDateString(bookingCreationDate)) {
    return {
      canBook: false,
      mustPayInFull: false,
      dueAtBooking: 0,
      schedule: [],
      totalPrice: 0,
      reasons: ['invalid_input']
    };
  }
  if (!paymentPlan || typeof paymentPlan.finalPaymentDue === 'undefined') {
    return {
      canBook: false,
      mustPayInFull: false,
      dueAtBooking: 0,
      schedule: [],
      totalPrice: 0,
      reasons: ['invalid_input']
    };
  }

  // Bookability checks that depend on departure date
  if (hasStartDate && compareYMD(bookingCreationDate, tripStartDate) >= 0) {
    return {
      canBook: false,
      mustPayInFull: false,
      dueAtBooking: 0,
      schedule: [],
      totalPrice: 0,
      reasons: ['after_trip_start']
    };
  }

  const deadlines = hasStartDate
    ? (bookingPlans || [])
        .map((p) => p?.bookingDeadline)
        .filter((d) => isValidDateString(d))
    : [];
  if (deadlines.length > 0) {
    const earliest = deadlines.sort((a, b) => compareYMD(a, b))[0];
    if (compareYMD(bookingCreationDate, earliest) > 0) {
      return {
        canBook: false,
        mustPayInFull: false,
        dueAtBooking: 0,
        schedule: [],
        totalPrice: 0,
        reasons: ['after_booking_deadline']
      };
    }
  }

  // Totals
  const basePriceFromPlans = round2(
    (bookingPlans || []).reduce((acc, p) => {
      const occ = Number(p?.occupancy) || 0;
      const ppp = Number(p?.pricePerPerson ?? p?.priceperperson) || 0;
      return acc + occ * ppp;
    }, 0)
  );

  // Calculate total add-on prices (all add-ons, regardless of dueAtBooking)
  const totalAddOnPrice = round2(
    (bookingAddOns || []).reduce((acc, addOn) => {
      const price = Number(addOn?.pricePerPerson ?? addOn?.priceperperson) || 0;
      return acc + price;
    }, 0)
  );

  // Calculate add-ons due at booking (to be added to deposit)
  const dueAtBookingAddOnsTotal = round2(
    (bookingAddOns || []).reduce((acc, addOn) => {
      const isDueAtBooking =
        addOn?.dueAtBooking ?? addOn?.dueatbooking ?? false;
      if (!isDueAtBooking) return acc;
      const price = Number(addOn?.pricePerPerson ?? addOn?.priceperperson) || 0;
      return acc + price;
    }, 0)
  );

  // Calculate add-ons NOT due at booking (for % deposit calculation)
  const notDueAtBookingAddOnsTotal = round2(
    totalAddOnPrice - dueAtBookingAddOnsTotal
  );

  const adjustment = Number(priceAdjustment) || 0;
  const totalPrice = Math.max(
    round2(basePriceFromPlans + totalAddOnPrice + adjustment),
    0
  );
  const paxCount = (bookingPlans || []).reduce(
    (acc, p) => acc + (Number(p?.occupancy) || 0),
    0
  );

  // Helper to store schedule entries with consistent rounding/validation
  const scheduleEntries = [];
  const pushEntry = (label, amount, date) => {
    const rounded = round2(amount);
    if (rounded <= 0) return;
    if (!isValidDateString(date)) return;
    scheduleEntries.push({ label, amount: rounded, date });
  };

  const resolveDueDate = (dueDays) => {
    if (!hasStartDate) {
      return null;
    }
    if (dueDays === null || dueDays === undefined) {
      return bookingCreationDate;
    }
    return subtractDays(tripStartDate, dueDays);
  };

  // Deposits (always due at booking)
  const depositFormula = parseDepositFormula(paymentPlan?.depositAmount);

  // For % deposits: calculate on (plans + non-due-at-booking add-ons), then add due-at-booking add-ons
  // For $ deposits: use the flat amount, then add due-at-booking add-ons
  const baseForDepositCalculation =
    depositFormula.type === '%'
      ? round2(basePriceFromPlans + notDueAtBookingAddOnsTotal + adjustment)
      : totalPrice;

  const baseDepositAmount = round2(
    computeDepositAmount(depositFormula, baseForDepositCalculation, paxCount)
  );

  // Add due-at-booking add-ons to the deposit amount
  const uncappedDepositAmount = round2(
    baseDepositAmount + dueAtBookingAddOnsTotal
  );

  // Cap deposit at total price to prevent overpayment (waterfall capping)
  const d1 = Math.min(uncappedDepositAmount, totalPrice);

  if (d1 > 0) {
    pushEntry('deposit', d1, bookingCreationDate);
  }

  // Track remaining balance after each payment
  let remainingBalance = Math.max(round2(totalPrice - d1), 0);
  // Post-deposit base for percentage calculations (remains constant)
  const postDepositBase = remainingBalance;

  // Parse all payment formulas to determine if all are percentage-based
  const secondFormula = parsePaymentAmountFormula(
    paymentPlan?.secondPaymentAmount
  );
  const thirdFormula = parsePaymentAmountFormula(
    paymentPlan?.thirdPaymentAmount
  );
  const fourthFormula = parsePaymentAmountFormula(
    paymentPlan?.fourthPaymentAmount
  );

  // Check if all payments are percentage-based
  const useGlobalBase = areAllPaymentsPercentageBased(
    depositFormula,
    secondFormula,
    thirdFormula,
    fourthFormula
  );

  // Second payment
  const sDue = coerceNumberOrNull(paymentPlan?.secondPaymentDue);
  if (secondFormula) {
    const amount = computePaymentAmount(
      secondFormula,
      postDepositBase,
      remainingBalance,
      paxCount,
      useGlobalBase,
      totalPrice
    );
    if (amount > 0) {
      pushEntry('second', amount, resolveDueDate(sDue));
      remainingBalance = Math.max(round2(remainingBalance - amount), 0);
    }
  }

  // Third payment
  const tDue = coerceNumberOrNull(paymentPlan?.thirdPaymentDue);
  if (thirdFormula) {
    const amount = computePaymentAmount(
      thirdFormula,
      postDepositBase,
      remainingBalance,
      paxCount,
      useGlobalBase,
      totalPrice
    );
    if (amount > 0) {
      pushEntry('third', amount, resolveDueDate(tDue));
      remainingBalance = Math.max(round2(remainingBalance - amount), 0);
    }
  }

  // Fourth payment
  const fDue = coerceNumberOrNull(paymentPlan?.fourthPaymentDue);
  if (fourthFormula) {
    const amount = computePaymentAmount(
      fourthFormula,
      postDepositBase,
      remainingBalance,
      paxCount,
      useGlobalBase,
      totalPrice
    );
    if (amount > 0) {
      pushEntry('fourth', amount, resolveDueDate(fDue));
      remainingBalance = Math.max(round2(remainingBalance - amount), 0);
    }
  }

  if (hasStartDate) {
    // Final due date (absolute) derived from days-before-start
    const finalDueDays = Number(paymentPlan?.finalPaymentDue) || 0;
    const finalDueDate = subtractDays(tripStartDate, finalDueDays);

    if (compareYMD(bookingCreationDate, finalDueDate) > 0) {
      reasons.push('final_due_in_past_requires_full_payment');
    }

    const sumBeforeFinal = round2(
      scheduleEntries.reduce((acc, entry) => acc + entry.amount, 0)
    );
    const finalAmount = Math.max(round2(totalPrice - sumBeforeFinal), 0);

    if (finalAmount > 0) {
      pushEntry('final', finalAmount, finalDueDate);
    }
  }

  // Sort for determinism (date asc, then label order)
  scheduleEntries.sort((a, b) => {
    const c = compareYMD(a.date, b.date);
    if (isNaN(c) || c === 0) {
      return (LABEL_ORDER[a.label] || 99) - (LABEL_ORDER[b.label] || 99);
    }
    return c;
  });

  const orderedEntries = scheduleEntries.map((entry) => ({ ...entry }));

  const dueTodayTotal = round2(
    orderedEntries.reduce((acc, entry) => {
      const cmp = compareYMD(entry.date, bookingCreationDate);
      if (!isNaN(cmp) && cmp <= 0) {
        return acc + entry.amount;
      }
      return acc;
    }, 0)
  );

  const mustPayInFull = Math.abs(dueTodayTotal - totalPrice) < 0.005;
  const futureSchedule = mustPayInFull
    ? []
    : orderedEntries
        .filter((entry) => {
          const cmp = compareYMD(entry.date, bookingCreationDate);
          return !isNaN(cmp) && cmp > 0;
        })
        .map(({ date, amount, label }) => ({ date, amount, label }));

  return {
    canBook: true,
    mustPayInFull,
    dueAtBooking: mustPayInFull ? round2(totalPrice) : dueTodayTotal,
    schedule: futureSchedule,
    totalPrice: round2(totalPrice),
    reasons: reasons.length ? reasons : undefined,
    allEntries: orderedEntries
  };
};

export default calculatePaymentScheduleNew;
